import React, { useEffect, useState } from 'react';
import Loader from 'pages/Test/localComponent/loader';
import '../broadcastNext.css';

const two = (value) => value.toString().padStart(2, '0');

const recordDuration = (seconds = 0) =>
  `${two(Math.floor(seconds / 3600))}:${two(Math.floor((seconds % 3600) / 60))}:${two(seconds % 60)}`;

// What the pill says, chosen by what is actually limiting the class rather than by how bad
// it is. Only 'link' — loss and round trip — may be called a connection problem. An encoder
// held back by the uplink budget, or by the machine it is running on, is a different fault
// with a different remedy, and naming either of them "weak network" is what sent teachers
// off to restart routers that were working.
const CAUSE_LABEL = {
  ok: 'Good',
  link: 'Weak connection',
  uplink: 'Upload limited',
  cpu: 'Device busy',
};

const CAUSE_TITLE = {
  ok: 'Connection is healthy',
  link: 'Packets are being lost or delayed between you and the class',
  uplink: 'Your upload cannot carry the video this class is sending — the quality will step down',
  cpu: 'This device cannot encode fast enough. Closing other apps will help more than the network will',
};

// Four bars, filled by quality. Drawn rather than iconified so the partial states read at a
// glance — react-icons only ships whole-signal glyphs.
const SignalBars = ({ quality = 'good' }) => {
  const filled = quality === 'good' ? 4 : quality === 'fair' ? 2 : 1;
  return (
    <span className="bn-signal" aria-hidden="true">
      {[0, 1, 2, 3].map((index) => (
        <i key={index} className={index < filled ? 'bn-on' : ''} style={{ height: 4 + index * 3 }} />
      ))}
    </span>
  );
};

/**
 * Live classroom header. Replaces the old floating TopRow — same data, no new sources:
 * recording state comes from the existing RTMP flow, the clock is local.
 */
const LiveTopBar = ({
  subjectInitial,
  subjectIcon,
  title,
  subtitle = 'In session',
  isRecordingState = 0,
  recordTimer = 0,
  canRecord = false,
  onRecordClick,
  isStudent = false,
  rightView = false,
  floating = false,
  quality = 'good',
  qualityCause = 'ok',
  netStats = null,
  lineSpeed = null,
  videoRung = null,
  speedTesting = false,
  speedDrop = null,
  onSpeedRetest,
  formatBytes,
  formatBitrate,
  formatSpeed,
}) => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 20000);
    return () => clearInterval(id);
  }, []);

  // Age of the reading, ticking on its own so "2m ago" does not sit frozen at "just now"
  // until the next measurement happens to land.
  const [ageTick, setAgeTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setAgeTick((n) => n + 1), 5000);
    return () => clearInterval(id);
  }, []);
  const measuredAgo = React.useMemo(() => {
    if (!lineSpeed) return ''
    const seconds = Math.max(Math.round((Date.now() - lineSpeed.at) / 1000), 0);
    if (seconds < 20) return 'just now'
    if (seconds < 90) return `${Math.round(seconds / 10) * 10}s ago`
    if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`
    return `${Math.round(seconds / 3600)}h ago`
  }, [lineSpeed, ageTick]);

  const recording = isRecordingState === 2;
  const recordPending = isRecordingState === 1;

  return (
    // Static above the inset stage; floated over the canvas in whiteboard mode, where the
    // side panel takes 25% of the screen and the bar has to stop at its edge.
    <div
      className={floating ? 'bn-live-top' : 'bn-header'}
      style={floating ? { right: rightView ? '25%' : 0 } : undefined}
    >
      <div className="bn-header-left">
        {/* The badge names the class, so it is the subject's own icon - the same one the
            schedule card shows - falling back to the subject's initial when no icon has
            been uploaded. This used to be the institute logo, which is identical for every
            class and so said nothing about which one you had joined. */}
        <div className="bn-org">{subjectIcon ? <img src={subjectIcon} alt="" /> : subjectInitial}</div>
        <div style={{ minWidth: 0 }}>
          <div className="bn-title">{title}</div>
          <div className="bn-subtitle">{subtitle}</div>
        </div>
      </div>

      <div className="bn-header-right">
        {/* Measured line speed, on screen from the moment the room is joined.

            This is a real transfer, not a figure derived from the call — upload for anyone
            publishing, download for a student — see networkSpeedTest.js for why WebRTC's
            own statistics cannot produce one, and why the direction matters. It is
            what the connection can carry, which is the question being asked; the arrows
            behind it are what the class is actually spending, and the round trip and loss
            beside them are how that spend is faring right now.

            The session total moved into the tooltip. It answers a billing question, not a
            "how is my internet" one, and it was the widest thing in the chip. */}
        {/* <button
          type="button"
          className={`bn-net-chip bn-net-${quality}${speedDrop ? ' bn-net-dropped' : ''}`}
          onClick={onSpeedRetest}
          disabled={speedTesting}
          title={[
            lineSpeed
              ? `Your connection measured ${formatSpeed(lineSpeed.bps)} ${lineSpeed.direction === 'down' ? 'down' : 'up'}, ${measuredAgo}`
              : 'Measuring your connection speed',
            speedDrop ? `Down from ${formatSpeed(speedDrop.from)} at the last check` : null,
            videoRung ? `Sending ${videoRung.label} — ceiling ${videoRung.ceiling} on this line` : null,
            netStats ? `This class is using ↑${formatBitrate(netStats.upBps)}bps ↓${formatBitrate(netStats.downBps)}bps` : null,
            netStats && netStats.rtt !== null ? `Round trip ${Math.round(netStats.rtt * 1000)}ms` : null,
            netStats ? `Packet loss ${(netStats.lossPct || 0).toFixed(1)}%` : null,
            netStats && Number.isFinite(netStats.availableOutgoingBitrate)
              ? `Headroom for the stream ${formatSpeed(netStats.availableOutgoingBitrate)}`
              : null,
            netStats ? `This class has used ${formatBytes(netStats.bytesSent + netStats.bytesReceived)}` : null,
            'Click to measure again',
          ]
            .filter(Boolean)
            .join('\n')}
        >
          <span className={`bn-net-speed${speedTesting ? ' bn-net-measuring' : ''}`}>
            {lineSpeed ? (
              <>
                <span className="bn-net-arrow">{lineSpeed.direction === 'down' ? '↓' : '↑'}</span>
                {formatSpeed(lineSpeed.bps)}
              </>
            ) : (
              'Measuring…'
            )}
          </span>

          {lineSpeed && measuredAgo !== 'just now' ? (
            <span className="bn-net-age">{measuredAgo}</span>
          ) : null}

          {speedDrop ? (
            <span className="bn-net-drop">▼ from {formatSpeed(speedDrop.from)}</span>
          ) : null}

          {videoRung ? (
            <>
              <span className="bn-net-sep" />
              <span className={`bn-net-rung bn-net-rung-${videoRung.reason}`}>
                {videoRung.reason === 'down' ? '▼' : '▲'} {videoRung.label}
              </span>
            </>
          ) : null}

          {netStats && Number.isFinite(netStats.availableOutgoingBitrate) ? (
            <>
              <span className="bn-net-sep" />
              <span className="bn-net-rate" title="Upload available to this class">
                ↑ {formatSpeed(netStats.availableOutgoingBitrate)}
              </span>
            </>
          ) : null}

          {netStats && netStats.rtt !== null ? (
            <>
              <span className="bn-net-sep" />
              <span className="bn-net-rate">{Math.round(netStats.rtt * 1000)} ms</span>
            </>
          ) : null}

          {netStats && netStats.lossPct >= 1 ? (
            <>
              <span className="bn-net-sep" />
              <span className="bn-net-loss">{netStats.lossPct.toFixed(1)}% loss</span>
            </>
          ) : null}

          {netStats ? (
            <>
              <span className="bn-net-sep" />
              <span className="bn-net-rate">
                ↑{formatBitrate(netStats.upBps)} ↓{formatBitrate(netStats.downBps)}
              </span>
            </>
          ) : null}
        </button>

        <span
          className={`bn-quality bn-quality-${quality}`}
          title={CAUSE_TITLE[qualityCause] ?? CAUSE_TITLE.ok}
        >
          <SignalBars quality={quality} />
          {CAUSE_LABEL[qualityCause] ?? CAUSE_LABEL.ok}
        </span> */}

        {!isStudent && canRecord ? (
          <button
            type="button"
            className="bn-rec-toggle"
            onClick={onRecordClick}
            aria-pressed={recording}
            title={recording ? 'Stop recording' : 'Record this class'}
          >
            <span className="bn-rec-label">
              <span className="bn-rec-dot" style={{ background: recording ? '#ef4444' : '#d9d5e3' }} />
              {recording ? recordDuration(recordTimer) : 'REC'}
            </span>
            {recordPending ? (
              <Loader style={{ fontSize: 14, color: '#594099' }} />
            ) : (
              <span className="bn-switch" data-on={recording}>
                <span />
              </span>
            )}
          </button>
        ) : null}

        <span className="bn-live-pill">
          <span className="bn-rec-dot" />
          Live · {two(now.getHours())}:{two(now.getMinutes())}
        </span>
      </div>
    </div>
  );
};

export default LiveTopBar;
